import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  Activity,
  TrendingDown,
  ShieldCheck,
  HelpCircle,
  Radio,
  BarChart3,
  Brain,
  Moon,
  Inbox,
  Clock,
} from "lucide-react";
import { minutesAgo } from "./api";
import InsightsBlurb from "./InsightsBlurb";

const STATE_CONFIG = {
  upward: {
    accent: "bg-signal-up",
    pillBg: "bg-signal-up-bg",
    pillText: "text-signal-up",
    icon: TrendingUp,
    label: "Market Alert",
  },
  volatile: {
    accent: "bg-signal-volatile",
    pillBg: "bg-signal-volatile-bg",
    pillText: "text-signal-volatile",
    icon: Activity,
    label: "Market Update",
  },
  stable: {
    accent: "bg-signal-stable",
    pillBg: "bg-signal-stable-bg",
    pillText: "text-signal-stable",
    icon: ShieldCheck,
    label: "Market Stable",
  },
  closed: {
    accent: "bg-slate-300",
    pillBg: "bg-slate-100",
    pillText: "text-slate-600",
    icon: Moon,
    label: "Markets Closed",
  },
};

const STAT_TOOLTIPS = {
  bond:
    "The 5-year Government of Canada bond yield. Canadian fixed mortgage rates closely track this benchmark — typically with a 1-3 day lag.",
  move:
    "How much the bond yield has shifted over the last 5 trading days, in basis points (1 bps = 0.01%).",
  updates:
    "How many lender updates we've processed since midnight Eastern. Proves the system is actively listening to the market today — every update could shift the rate landscape.",
};

function formatCountdown(totalSec) {
  if (totalSec == null || totalSec <= 0) return null;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Live-ticking "Reopens in Xh YYm" pill. Only rendered when the backend
 * sets `show_countdown=true` — which only fires when next open is ≤ 24h
 * away. Hides itself past zero (front-end safety; backend should already
 * have flipped market_closed → false by then on the next poll).
 */
function useCountdown(nextOpenIso, enabled) {
  const [remaining, setRemaining] = useState(() => {
    if (!enabled || !nextOpenIso) return null;
    const s = Math.floor((new Date(nextOpenIso).getTime() - Date.now()) / 1000);
    return s > 0 ? s : null;
  });
  useEffect(() => {
    if (!enabled || !nextOpenIso) {
      setRemaining(null);
      return undefined;
    }
    const tick = () => {
      const s = Math.floor(
        (new Date(nextOpenIso).getTime() - Date.now()) / 1000
      );
      setRemaining(s > 0 ? s : null);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [nextOpenIso, enabled]);
  return remaining;
}

export default function MarketSignalCard({ signal, loading }) {
  const isClosed = !!signal?.market_closed;
  const stateKey = isClosed ? "closed" : signal?.state || "stable";
  const cfg = STATE_CONFIG[stateKey] || STATE_CONFIG.stable;
  const Icon = cfg.icon;
  const Trend = signal?.d5_change_bps >= 0 ? TrendingUp : TrendingDown;
  const countdownSec = useCountdown(
    signal?.market_next_open_at_iso,
    !!signal?.show_countdown
  );
  const countdownLabel = formatCountdown(countdownSec);

  const eventsTracked = signal?.events_tracked;
  const lendersProfiled = signal?.lenders_profiled;
  const accuracy30d = signal?.accuracy_30d_pct;

  return (
    <section
      data-testid="market-signal-card"
      className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm animate-fade-up"
    >
      {/* Top accent strip */}
      <div className={`h-1.5 w-full ${cfg.accent}`} aria-hidden="true" />

      <div className="px-5 py-6 sm:px-7 sm:py-7">
        {/* Live row */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 font-body">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                signal?.bond_is_live
                  ? "bg-signal-stable animate-live-pulse motion-reduce:animate-none"
                  : "bg-slate-400"
              }`}
              aria-label={signal?.bond_is_live ? "Live" : "Official close"}
            />
            {signal?.bond_is_live ? (
              <>
                <span data-testid="market-signal-live-label">Live</span>
                <span className="text-slate-300">·</span>
                <span data-testid="market-signal-updated">
                  Updated {minutesAgo(signal?.updated_at_iso) || "each business day"}
                </span>
              </>
            ) : (
              <span data-testid="market-signal-updated">
                {signal?.bond_as_of_label ||
                  `Updated ${minutesAgo(signal?.updated_at_iso) || "each business day"}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {countdownLabel && (
              <span
                data-testid="market-signal-countdown"
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest font-body bg-slate-100 text-slate-700 tabular-nums"
                title={`Markets reopen at ${signal?.market_next_open_time_label || "8:00 AM ET"}`}
              >
                <Clock size={12} strokeWidth={2.5} />
                Reopens in {countdownLabel}
              </span>
            )}
            <span
              data-testid="market-signal-state-pill"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest font-body ${cfg.pillBg} ${cfg.pillText}`}
            >
              <Icon size={12} strokeWidth={2.5} />
              {cfg.label}
            </span>
          </div>
        </div>

        {/* Tool brand + credibility strip */}
        <div className="mb-4" data-testid="market-signal-brand">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red font-body">
            Rate Intelligence
          </div>
          <div className="text-[11px] text-slate-500 font-body mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {eventsTracked != null && (
              <span data-testid="credibility-events">
                <span className="font-bold tabular-nums text-brand-navy">
                  {eventsTracked.toLocaleString()}+
                </span>{" "}
                lender rate moves tracked
              </span>
            )}
            {lendersProfiled != null && (
              <>
                <span className="text-slate-300" aria-hidden="true">·</span>
                <span data-testid="credibility-lenders">
                  <span className="font-bold tabular-nums text-brand-navy">
                    {lendersProfiled}
                  </span>{" "}
                  lenders profiled
                </span>
              </>
            )}
            {accuracy30d != null && (
              <>
                <span className="text-slate-300" aria-hidden="true">·</span>
                <span data-testid="credibility-accuracy">
                  <span className="font-bold tabular-nums text-emerald-600">
                    {accuracy30d}%
                  </span>{" "}
                  30-day accuracy
                </span>
              </>
            )}
          </div>
        </div>

        {/* Headline */}
        <p
          data-testid="market-signal-headline"
          className="text-xl sm:text-2xl font-bold tracking-tight leading-snug text-brand-navy font-display"
        >
          {loading ? (
            <span className="inline-block w-3/4 h-7 bg-slate-100 rounded animate-pulse" />
          ) : (
            signal?.headline || "Loading market signal…"
          )}
        </p>

        {/* Hello Mortgage Insights — LLM-written analyst commentary based
            on today's live data. Hidden gracefully when no LLM is available. */}
        <div className="mt-5">
          <InsightsBlurb context="general" />
        </div>

        {/* Stat grid */}
        <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4 text-left">
          <Stat
            label="5-yr GoC bond"
            tooltip={STAT_TOOLTIPS.bond}
            value={signal?.bond_yield != null ? `${Number(signal.bond_yield).toFixed(2)}%` : "—"}
            sub={
              signal?.d1_change_bps != null
                ? `${signal.d1_change_bps >= 0 ? "+" : ""}${signal.d1_change_bps} bps · 1d`
                : null
            }
            subColor={
              signal?.d1_change_bps >= 3
                ? "text-signal-up"
                : signal?.d1_change_bps <= -3
                ? "text-signal-stable"
                : "text-slate-400"
            }
          />
          <Stat
            label="5-day move"
            tooltip={STAT_TOOLTIPS.move}
            value={
              signal?.d5_change_bps != null
                ? `${signal.d5_change_bps >= 0 ? "+" : ""}${signal.d5_change_bps}`
                : "—"
            }
            valueClass={
              signal?.d5_change_bps >= 5
                ? "text-signal-up"
                : signal?.d5_change_bps <= -5
                ? "text-signal-stable"
                : "text-brand-navy"
            }
            sub="bps"
            icon={
              signal?.d5_change_bps != null && Math.abs(signal.d5_change_bps) >= 5 ? Trend : null
            }
          />
          <Stat
            label="Lender Updates Today"
            tooltip={STAT_TOOLTIPS.updates}
            value={
              signal?.lender_updates_today != null
                ? `${signal.lender_updates_today}`
                : "—"
            }
            valueClass={
              signal?.lender_updates_today >= 5
                ? "text-brand-red"
                : signal?.lender_updates_today >= 1
                ? "text-brand-navy"
                : "text-slate-500"
            }
            sub={
              signal?.lender_updates_today === 0
                ? "QUIET TODAY"
                : signal?.lender_updates_today === 1
                ? "TODAY"
                : "TODAY"
            }
            icon={signal?.lender_updates_today >= 1 ? Inbox : null}
          />
        </div>

        {/* ── Below the stats: the moat positioning ──────────────────────── */}
        <div className="mt-7 pt-6 border-t border-slate-100">
          <p
            data-testid="market-signal-moat"
            className="text-sm sm:text-[15px] text-brand-navy font-display leading-snug"
          >
            <span className="font-bold">This isn't bond watching.</span>{" "}
            <span className="text-slate-600 font-body">
              We pattern-match today's market against{" "}
              {eventsTracked ? `${eventsTracked.toLocaleString()}+` : "thousands of"} real lender
              rate moves to predict which lenders will move, when, and by how much.
            </span>
          </p>

          {/* Three pillars */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Pillar
              icon={Radio}
              accentClass="text-brand-red"
              title="Live Bond Feed"
              body="5-yr Canada bond + BoC rate. Live intraday when markets are open, with the official Bank of Canada close as the labelled fallback — the same inputs your lender uses to price you."
              testid="pillar-live"
            />
            <Pillar
              icon={BarChart3}
              accentClass="text-brand-navy"
              title={eventsTracked ? `${eventsTracked.toLocaleString()}+ Historical Moves` : "Historical Moves"}
              body="Every lender rate change in our inbox, scored. We know each lender's reaction lag and trigger threshold."
              testid="pillar-historical"
            />
            <Pillar
              icon={Brain}
              accentClass="text-emerald-600"
              title="Self-Improving"
              body={
                accuracy30d != null
                  ? `Every new rate change grades our last call. Last 30 days: ${accuracy30d}% accuracy.`
                  : "Every new rate change grades our last call. The system recalibrates weekly."
              }
              testid="pillar-self-improving"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  sub,
  valueClass = "text-brand-navy",
  subColor = "text-slate-500",
  icon: Icon,
  tooltip,
}) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 font-body mb-1.5">
        <span>{label}</span>
        {tooltip && (
          <span className="group relative inline-flex">
            <HelpCircle
              size={11}
              className="text-slate-300 hover:text-slate-500 transition-colors cursor-help"
              aria-label={`Learn more about ${label}`}
              tabIndex={0}
            />
            <span
              role="tooltip"
              className="hidden group-hover:block group-focus-within:block absolute left-1/2 top-full -translate-x-1/2 mt-2 w-56 z-20 bg-brand-navy text-white text-[11px] leading-snug font-body normal-case tracking-normal rounded-lg p-3 shadow-xl"
            >
              {tooltip}
            </span>
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span
          className={`text-xl sm:text-2xl font-bold tabular-nums tracking-tight font-display ${valueClass}`}
        >
          {value}
        </span>
        {Icon && <Icon size={14} className={valueClass} strokeWidth={2.5} />}
      </div>
      {sub && (
        <div className={`text-[11px] font-medium font-display mt-0.5 ${subColor}`}>{sub}</div>
      )}
    </div>
  );
}

function Pillar({ icon: Icon, accentClass, title, body, testid }) {
  return (
    <div data-testid={testid} className="flex items-start gap-2.5">
      <Icon size={16} className={`${accentClass} mt-0.5 shrink-0`} strokeWidth={2.5} />
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-navy font-body">
          {title}
        </div>
        <div className="text-[12px] text-slate-600 font-body leading-relaxed mt-1">{body}</div>
      </div>
    </div>
  );
}
